import { useCallback, useContext, useMemo } from 'react';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui/Stack';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { GroupListingsModal } from 'src/modals/GroupListings';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalFooter,
} from 'src/modals/Modal/Modal.styled';
import { ChevronRightIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, ListingGroup } from 'src/WebApiController';

import * as drawerStyles from '../MobileListingDrawer.css';
import { useMobileListingDrawerContext } from '../MobileListingDrawerContext';
import { useGroupOperations } from './MobileListingGroupSideView.hooks';
import * as styles from './MobileListingSideView.css';

export const MobileListingGroupSideView = ({
  event,
  listingGroup,
}: {
  event: Event;
  listingGroup: ListingGroup;
}) => {
  const { onSelectListingGroup } = useMobileListingDrawerContext();
  const { setModal, closeModal } = useContext(ModalContext);
  const onEditGroup = useCallback(() => {
    setModal({
      children: (
        <GroupListingsModal events={[event]} listingGroup={listingGroup} />
      ),
      clickOutsideToClose: true,
      size: 'fullscreen',
    });
  }, [event, listingGroup, setModal]);

  const onConfirmUngroup = useCallback(() => {
    setModal({
      children: (
        <ConfirmUngroupModal
          listingGroup={listingGroup}
          onConfirm={() => {
            closeModal();
            onSelectListingGroup(null);
          }}
        />
      ),
      clickOutsideToClose: true,
      size: 'fullscreen',
    });
  }, [closeModal, listingGroup, onSelectListingGroup, setModal]);

  const actions = useMemo<{ title: ContentId; onClick: () => void }[]>(() => {
    return [
      { title: ContentId.Edit, onClick: onEditGroup },
      { title: ContentId.Ungroup, onClick: onConfirmUngroup },
    ];
  }, [onConfirmUngroup, onEditGroup]);

  return (
    <Stack
      direction="column"
      width="full"
      alignItems="center"
      gap="s"
      className={drawerStyles.drawerInnerContent}
    >
      {actions.map((action, index) => {
        return (
          <Stack
            role="button"
            tabIndex={index}
            key={action.title}
            width="full"
            justifyContent="spaceBetween"
            alignItems="center"
            className={styles.sectionalItemContainer}
            onClick={action.onClick}
          >
            <Content id={action.title} />
            <ChevronRightIcon size={vars.iconSize.s} />
          </Stack>
        );
      })}
    </Stack>
  );
};

const ConfirmUngroupModal = ({
  listingGroup,
  onConfirm,
}: {
  listingGroup: ListingGroup;
  onConfirm: () => void;
}) => {
  const { isLoading, onUngroupListings } = useGroupOperations(listingGroup);
  return (
    <>
      <CancellableFormHeader>
        <ConnectedEventEntityHeader
          title={<Content id={ContentId.Ungroup} />}
        />
      </CancellableFormHeader>

      <ModalBody>
        <ModalBodyDataContainer style={{ height: 'auto !important' }}>
          <Content id={ContentId.AreYouSure} />
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <OkButton
          disabled={isLoading}
          onClick={() => onUngroupListings(onConfirm)}
          textContentId={ContentId.OK}
        />
      </ModalFooter>
    </>
  );
};
